import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { ApolloError } from "@apollo/client";
import { GraphQLError } from "graphql";
import { Form, FormSpy } from "react-final-form";
import { FormState } from "final-form";
import { ThemeContext } from "styled-components";
import { format } from "date-fns";
import { TenantContext } from "v3/context/tenant";
import { CheckoutContext } from "v3/context/checkout";
import { LocalizationContext } from "v3/context/localization";
import { useCountryOnLoad } from "v3/hooks/useCountryOnLoad";
import { usePhoneNumberCountryCodeOnLoad } from "v3/hooks/usePhoneNumberCountryCodeOnLoad";
import { FieldInput } from "@pepdirect/v3/ui/FieldInput";
import { CheckoutErrorContext } from "v3/context/checkoutError";
import { Button } from "@pepdirect/v3/ui/Button";
import { PlusIconSvg } from "@pepdirect/v3/ui/icons";
import { SectionHeading } from "v3/components/checkout/SectionHeading";
import { GiftForm } from "v3/components/forms/GiftForm";
import { RequestedDeliveryDate } from "v3/components/checkout/RequestedDeliveryDate";
import { AddressVerificationModal } from "v3/components/modals/AddressVerificationModal";
import { SavedAddresses } from "v3/components/checkout/SavedAddresses";
import { NewShippingAddressForm } from "v3/components/forms/NewShippingAddressForm";
import { LegalCopy } from "v3/components/checkout/LegalCopy";
import {
  useApplySavedShippingAddressMutation,
  useIsAddressValidForTaxLazyQuery,
  useRemoveGiftInfoMutation,
  useSaveEnteredContactInfoMutation,
  useSaveGiftInfoMutation,
  useSaveShippingOptionsMutation,
  useSaveSuggestedAddressMutation,
  useVerifyAddressMutation,
  ValidationRequirement,
} from "v3/services/graphql/generated";
import { convertISO8601ToBrowserDate } from "@pepdirect/v3/helpers/date";
import {
  additionalPhoneNumberValidation,
  ShippingAddressFormValues,
  shippingAddressValidation,
} from "v3/helpers/validation";
import {
  getInitialValuesShippingStep,
  getSelectedAddress,
  isEnteredAddressValidated,
  showShipFormOnLoad,
  sortAddressesByDefault,
} from "v3/helpers/address";
import {
  parseIsAddressValidForTaxGraphQLErrors,
  parseVerifyAddressGraphQLErrors,
} from "v3/helpers/errors";
import { logAddressVerified } from "@pepdirect/v3/helpers/analyticsLogger";
import { AutocompletedAddressValues } from "@pepdirect/v3/types";
import { SuggestedAddress } from "v3/types/error";
import { appIds } from "@pepdirect/v3/constants";
import { StyledFieldSet } from "@pepdirect/v3/ui/fields/base";
import {
  StyledAddButton,
  StyledAdditionalPhoneNumber,
  StyledAlert,
  StyledCouponCta,
  StyledHeading,
  StyledSavedAddresses,
} from "./index.styled";

interface ShippingStepProps {
  continueToNextStep: () => void;
}

export const ShippingStep: FC<ShippingStepProps> = ({ continueToNextStep }) => {
  const [applySavedShippingAddress] = useApplySavedShippingAddressMutation();
  const [saveEnteredContactInfo] = useSaveEnteredContactInfoMutation();
  const [saveSuggestedAddress] = useSaveSuggestedAddressMutation();
  const [verifyAddress] = useVerifyAddressMutation();
  const [saveGiftInfo] = useSaveGiftInfoMutation();
  const [removeGiftInfo] = useRemoveGiftInfoMutation();
  const [saveRequestedDeliveryDate] = useSaveShippingOptionsMutation();
  const [isAddressValidForTax] = useIsAddressValidForTaxLazyQuery();

  const { tenant, isStateRequired } = useContext(TenantContext);
  const {
    checkoutSession,
    isLoading,
    setCheckoutSession,
    setIsLoading,
    setScrollToCouponInput,
  } = useContext(CheckoutContext);
  const {
    clearAllErrors,
    handleCheckoutErrors,
    handleUniversalErrors,
    setErrorMsg,
  } = useContext(CheckoutErrorContext);
  const theme = useContext(ThemeContext);
  const { genericErrorMsg, localization } = useContext(LocalizationContext);

  // TEMP FIX DUE TO TIME CONSTRAINTS, TODO FIX IN:
  // BE: https://pepsico-ecomm.atlassian.net/browse/PCK-1003
  // FE: https://pepsico-ecomm.atlassian.net/browse/PCK-1020
  const isSSTPL = tenant?.id === appIds.SodaStreamPL;

  const { requireValidation } =
    tenant?.contactConfig.shippingAddressConfig || {};
  // a user can manually bypass the address verification modal if presented
  const allowBypassAddressVerification =
    requireValidation === ValidationRequirement.Optional;
  // the tenant has configured skipping verification entirely
  const allowSkipAddressVerification =
    requireValidation === ValidationRequirement.Never;

  const [shipSelectedFromAutocomplete, setShipSelectedFromAutocomplete] =
    useState<AutocompletedAddressValues | null>(null);

  const { shippingMethodsEnabled } = tenant?.checkout?.shippingOptions || {};

  const {
    shippingHeadingText = "Shipping",
    addressHeadingText = "Address",
    additionalPhoneNumberLabel = "Additional Order Phone Number (Optional)",
    savedAddressHeadingText = "Choose a shipping address",
    addAddressHeadingText = "Add a New Address",
    addAddressButtonText = "Add a new address",
    shipAlertText = "At this time we do not ship to P.O. boxes, Hawaii, or Alaska.",
    continueToPaymentButtonText = "Continue To Payment",
    continueToShippingMethodButtonText = "Continue to Shipping",
  } = localization?.checkout?.shippingStep || {};
  const { addCouponButtonText = "Have a coupon code?" } =
    localization?.checkout?.couponCode || {};
  const { unableToUsePoBox = "At this time we do not ship to P.O. boxes." } =
    localization?.checkout?.errors || {};

  const headingText = shippingMethodsEnabled
    ? addressHeadingText
    : shippingHeadingText;
  const giftAddressHeadingText =
    localization?.checkout?.giftForm?.shippingAddressHeadingText ||
    "Recipient's delivery address";
  const continueButtonText = shippingMethodsEnabled
    ? continueToShippingMethodButtonText
    : continueToPaymentButtonText;

  const {
    activeAccount,
    contactInfo,
    giftInfo,
    id,
    price,
    requestedDeliveryDate: savedRequestedDeliveryDate,
    shippingAddresses,
    user,
  } = checkoutSession || {};
  const { shippingAddress } = contactInfo || {};

  const { managedAddressesOnly } = activeAccount || {};
  const isGuest = !user;
  const hasSavedShippingAddresses = !!shippingAddresses?.length;
  const countriesAllowed = useMemo(
    () => tenant?.contactConfig?.shippingAddressConfig?.countriesAllowed || [],
    [tenant]
  );
  const statesConfig = useMemo(
    () => tenant?.contactConfig?.shippingAddressConfig?.statesConfig || [],
    [tenant]
  );
  const isGiftingEnabled = useMemo(
    () => tenant?.giftConfig?.giftingEnabled,
    [tenant]
  );
  const showRequestedDelivery = useMemo(
    () => tenant?.userExperience?.hasRequestedDeliveryDate,
    [tenant]
  );
  const maxDeliveryWindowInDays = useMemo(
    () => tenant?.shippingConfig.maxDeliveryWindowInDays,
    [tenant]
  );
  const showCompany = useMemo(
    () => tenant?.contactConfig.shippingAddressConfig.companyName,
    [tenant]
  );
  const showTaxId = useMemo(
    () => !!tenant?.contactConfig.shippingAddressConfig.taxId,
    [tenant]
  );
  const { country, code: countryCode } =
    usePhoneNumberCountryCodeOnLoad(tenant);

  const { countryOnLoad } = useCountryOnLoad({
    countriesAllowed,
    // don't set the country of a saved address
    ...(shippingAddress?.addressId
      ? {}
      : { countryCode: shippingAddress?.countryCode }),
  });

  const sortedAddressesByDefault = useMemo(
    () => sortAddressesByDefault(shippingAddresses || []),
    [shippingAddresses]
  );
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    getSelectedAddress({
      addresses: sortedAddressesByDefault,
      contactInfo,
      isGuest,
    })
  );
  const [showNewShipForm, setShowNewShipForm] = useState(
    showShipFormOnLoad({
      addresses: sortedAddressesByDefault,
      contactInfo,
      isGuest,
    })
  );
  const [suggestedAddress, setSuggestedAddress] =
    useState<SuggestedAddress | null>(null);
  const [isAddressVerificationModalOpen, setIsAddressVerificationModalOpen] =
    useState(false);
  const [isGiftFormOpen, setIsGiftFormOpen] = useState(!!giftInfo);
  const [requestedDeliveryDate, setRequestedDeliveryDate] =
    useState<Date | null>(
      savedRequestedDeliveryDate
        ? convertISO8601ToBrowserDate(savedRequestedDeliveryDate)
        : null
    );
  const [showUSShipAlert, setShowUSShipAlert] = useState(false);
  const [hasInvalidZip, setHasInvalidZip] = useState(false);

  useEffect(() => {
    if (selectedAddressId) setShowNewShipForm(false);
  }, [selectedAddressId]);

  const handleAddNewShip = () => {
    setSelectedAddressId(null);
    setShowNewShipForm(true);
  };

  const handleKeepEnteredAddress = async () => {
    continueToNextStep();
    setIsLoading(false);
  };

  const resetState = () => {
    setHasInvalidZip(false);
    setSuggestedAddress(null);
  };

  const closeAddressVerificationModal = () => {
    setIsAddressVerificationModalOpen(false);
    resetState();
  };

  const handleValidation = async (values: ShippingAddressFormValues) => {
    if (managedAddressesOnly) {
      return additionalPhoneNumberValidation({
        values,
        localization: localization?.forms?.validation,
        country,
      });
    }
    if (selectedAddressId) return;
    return shippingAddressValidation({
      isGiftFormOpen,
      isGuest,
      isNewShipFormOpen: showNewShipForm,
      isStateRequired,
      values,
      showTaxId,
      localization: localization?.forms?.validation,
      addressLocalization: localization?.forms?.address,
      country,
      showCompany,
    });
  };

  const handleSuggestedAddress = async () => {
    if (suggestedAddress) {
      try {
        setIsLoading(true);
        const { data } = await saveSuggestedAddress({
          variables: {
            id,
            input: suggestedAddress,
          },
        });
        if (data?.saveSuggestedAddress) {
          setCheckoutSession(data?.saveSuggestedAddress);
        }
      } catch (e) {
        handleCheckoutErrors(e);
        setIsLoading(false);
        return;
      }
    }

    continueToNextStep();
    setIsLoading(false);
  };

  const onSubmit = async (values: ShippingAddressFormValues) => {
    clearAllErrors();
    resetState();
    setIsLoading(true);

    if (requestedDeliveryDate) {
      try {
        const { data } = await saveRequestedDeliveryDate({
          variables: {
            id,
            input: {
              requestedDeliveryDate: format(
                new Date(requestedDeliveryDate),
                "yyyy-MM-dd"
              ),
            },
          },
        });
        if (data?.saveShippingOptions) {
          setCheckoutSession(data.saveShippingOptions);
        }
      } catch (e) {
        handleCheckoutErrors(e);
        setIsLoading(false);
        return;
      }
    }

    const {
      additionalPhoneNumber,
      giftInfo: giftInfoFormValues,
      shippingAddress,
    } = values;

    if (isGiftFormOpen && giftInfoFormValues) {
      const { gifteeEmail, gifteeMessage, giftorName } = giftInfoFormValues;
      try {
        const { data } = await saveGiftInfo({
          variables: {
            id,
            input: {
              gifteeEmail,
              gifteeMessage,
              giftorName,
            },
          },
        });
        if (data?.saveGiftInfo) {
          setCheckoutSession(data.saveGiftInfo);
        }
      } catch (e) {
        handleCheckoutErrors(e);
        setIsLoading(false);
        return;
      }
    }

    // use case is if a user previously entered gift info, but then
    // changed their mind and went back and unchecked the gift form
    if (!isGiftFormOpen && giftInfo) {
      try {
        const { data } = await removeGiftInfo({
          variables: { id },
        });
        if (data?.removeGiftInfo) {
          setCheckoutSession(data.removeGiftInfo);
        }
      } catch (e) {
        handleCheckoutErrors(e);
        setIsLoading(false);
        return;
      }
    }

    if (selectedAddressId) {
      try {
        const { data } = await applySavedShippingAddress({
          variables: {
            id,
            addressId: selectedAddressId,
            additionalPhoneNumber,
          },
        });
        if (data?.applySavedShippingAddress) {
          setCheckoutSession(data.applySavedShippingAddress);
        }
      } catch (e) {
        handleCheckoutErrors(e);
        setIsLoading(false);
        return;
      }
    } else {
      const { email, phone, state, country, emailOptIn, ...rest } =
        shippingAddress;

      const isAddressValidated = isEnteredAddressValidated({
        autocompletedAddress: shipSelectedFromAutocomplete,
        checkoutSessionAddress: contactInfo?.shippingAddress,
        enteredAddress: shippingAddress,
        isStateRequired,
      });

      try {
        const { data } = await saveEnteredContactInfo({
          variables: {
            id,
            input: {
              email,
              phone,
              emailOptIn,
              shipping: {
                ...rest,
                countryCode: country.value,
                state: state?.value,
                isAddressValidated,
              },
            },
          },
        });
        if (data?.saveEnteredContactInfo) {
          setCheckoutSession(data.saveEnteredContactInfo);
        }
      } catch (e) {
        handleCheckoutErrors(e);
        setIsLoading(false);
        return;
      }

      let zipIsInvalid = false;

      // Certain tenants are allowed to bypass/skip address verification. However,
      // the zip must still be valid for tax purposes, so they will not be able
      // to bypass if their zip is invalid.
      //
      // Since we (currently) only need to calculate taxes if a US address, skip
      // this check for all other countries.
      if (
        (allowBypassAddressVerification || allowSkipAddressVerification) &&
        country.value === "US"
      ) {
        try {
          const { city, line1, zip, line2 = "" } = rest;
          const { error } = await isAddressValidForTax({
            variables: {
              input: {
                line1,
                line2,
                city,
                state: state?.value,
                zip,
                countryCode: country.value,
              },
            },
          });
          if (error) throw error;
        } catch (e) {
          if (e instanceof ApolloError && e.graphQLErrors.length) {
            const hasUniversalError = handleUniversalErrors(
              e.graphQLErrors as GraphQLError[]
            );
            if (hasUniversalError) return;

            const { hasInvalidZip } = parseIsAddressValidForTaxGraphQLErrors(
              e.graphQLErrors as GraphQLError[]
            );

            if (hasInvalidZip) {
              setHasInvalidZip(true);
              // needed since state won't be updated in time for next function
              zipIsInvalid = true;
            } else {
              setErrorMsg(genericErrorMsg);
              setIsLoading(false);
              return;
            }
          } else {
            setErrorMsg(genericErrorMsg);
            setIsLoading(false);
            return;
          }
        }
      }

      if (
        !allowSkipAddressVerification &&
        (zipIsInvalid || !isAddressValidated)
      ) {
        try {
          await verifyAddress({
            variables: {
              id,
            },
          });
          logAddressVerified();

          if (zipIsInvalid) {
            // even if the verification service says the address is good, if the
            // tax service says the zip is invalid we show the invalid zip
            // code modal with no suggestion
            setIsAddressVerificationModalOpen(true);
            setIsLoading(false);
            return;
          }
        } catch (e) {
          if (e instanceof ApolloError && e.graphQLErrors.length) {
            const hasUniversalError = handleUniversalErrors(
              e.graphQLErrors as GraphQLError[]
            );
            if (hasUniversalError) return;

            const { suggestedAddress, hasPoBoxError, hasGenericError } =
              parseVerifyAddressGraphQLErrors(
                e.graphQLErrors as GraphQLError[]
              );

            // show PO box error first since we can't ship there
            if (hasPoBoxError) {
              setErrorMsg(unableToUsePoBox);
              // then show invalid zip errors with or without suggestion
            } else if (zipIsInvalid) {
              setSuggestedAddress(suggestedAddress);
              setIsAddressVerificationModalOpen(true);
              // then show generic error if any
            } else if (hasGenericError) {
              setErrorMsg(genericErrorMsg);
              // now the only option left is to show modal with/without a suggestion
            } else {
              setSuggestedAddress(suggestedAddress);
              setIsAddressVerificationModalOpen(true);
            }
          } else {
            setErrorMsg(genericErrorMsg);
          }

          setIsLoading(false);
          return;
        }
      }
    }

    continueToNextStep();
    setIsLoading(false);
  };

  return (
    <>
      <AddressVerificationModal
        closeModal={closeAddressVerificationModal}
        enteredAddress={contactInfo?.shippingAddress || {}}
        handleKeepEnteredAddress={handleKeepEnteredAddress}
        handleSuggestedAddress={handleSuggestedAddress}
        isOpen={isAddressVerificationModalOpen}
        suggestedAddress={suggestedAddress}
        hasInvalidZip={hasInvalidZip}
      />
      <Form
        initialValues={getInitialValuesShippingStep({
          contactInfo,
          countriesAllowed,
          countryOnLoad,
          giftInfo,
          statesConfig,
        })}
        keepDirtyOnReinitialize
        onSubmit={onSubmit}
        validate={handleValidation}
        render={({ handleSubmit, valid }) => (
          <form onSubmit={handleSubmit}>
            <FormSpy
              subscription={{ values: true, modified: true }}
              onChange={({
                modified,
                values,
              }: FormState<ShippingAddressFormValues>) => {
                if (modified && Object.keys(modified).length > 0) {
                  setShowUSShipAlert(
                    values.shippingAddress?.country?.value === "US"
                  );
                }
              }}
            />
            {isSSTPL && !price?.discount && (
              <StyledCouponCta>
                <Button
                  onClick={() => setScrollToCouponInput(true)}
                  type="text"
                >
                  {addCouponButtonText}
                </Button>
              </StyledCouponCta>
            )}
            <StyledFieldSet disabled={isLoading}>
              {showRequestedDelivery && maxDeliveryWindowInDays && (
                <RequestedDeliveryDate
                  maxDeliveryWindowInDays={maxDeliveryWindowInDays}
                  requestedDeliveryDate={requestedDeliveryDate}
                  setRequestedDeliveryDate={setRequestedDeliveryDate}
                />
              )}
              <SectionHeading>{headingText}</SectionHeading>
              {isGiftingEnabled && (
                <GiftForm
                  isGiftFormOpen={isGiftFormOpen}
                  isGuest={isGuest}
                  setIsGiftFormOpen={() =>
                    setIsGiftFormOpen((isOpen) => !isOpen)
                  }
                />
              )}
              {hasSavedShippingAddresses && (
                <>
                  <StyledHeading size="xs" level="h3">
                    {`${
                      isGiftFormOpen
                        ? giftAddressHeadingText
                        : savedAddressHeadingText
                    }`}
                  </StyledHeading>
                  <StyledSavedAddresses>
                    <SavedAddresses
                      addresses={sortedAddressesByDefault}
                      selectedAddressId={selectedAddressId}
                      setSelectedAddressId={setSelectedAddressId}
                    />
                  </StyledSavedAddresses>
                  {!managedAddressesOnly && !showNewShipForm && (
                    <StyledAddButton>
                      <Button type="text" onClick={handleAddNewShip}>
                        <PlusIconSvg color={theme.button.text.color} />
                        <span>{addAddressButtonText}</span>
                      </Button>
                    </StyledAddButton>
                  )}
                </>
              )}
              {showNewShipForm && (
                <>
                  {hasSavedShippingAddresses && (
                    <StyledHeading level="h2" size="s">
                      {addAddressHeadingText}
                    </StyledHeading>
                  )}
                  <NewShippingAddressForm
                    countryOnLoad={countryOnLoad}
                    enteredContactInfo={contactInfo}
                    handleRetrieveAddressError={(e: unknown) =>
                      handleCheckoutErrors(e)
                    }
                    localization={localization}
                    setShipSelectedFromAutocomplete={
                      setShipSelectedFromAutocomplete
                    }
                    shipSelectedFromAutocomplete={shipSelectedFromAutocomplete}
                    showCompany={showCompany}
                    showEmail={isGuest && !isGiftFormOpen}
                    showEmailOptIn={isGuest}
                    tenant={tenant}
                    showTaxId={showTaxId}
                  />
                </>
              )}

              {managedAddressesOnly && (
                <StyledAdditionalPhoneNumber>
                  <FieldInput
                    label={additionalPhoneNumberLabel}
                    name="additionalPhoneNumber"
                    autoComplete="tel-national"
                    inlineInputText={countryCode}
                  />
                </StyledAdditionalPhoneNumber>
              )}

              <Button
                htmlType="submit"
                disabled={
                  showNewShipForm || isGiftFormOpen
                    ? !valid
                    : !selectedAddressId
                }
                loading={isLoading}
              >
                {continueButtonText}
              </Button>
            </StyledFieldSet>
          </form>
        )}
      />
      <LegalCopy customText={tenant?.brandConfig.customTexts || []} />
      {showUSShipAlert && (
        <StyledAlert type="info">{shipAlertText}</StyledAlert>
      )}
    </>
  );
};
